import {useCallback, useEffect, useRef, useState} from "react";

import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";

import "./index.scss";

// ── Check browser support ─────────────────────────────────────────────────────

const SpeechRecognition =
	typeof window !== "undefined"
		? window.SpeechRecognition || window.webkitSpeechRecognition
		: null;

// ── Component ─────────────────────────────────────────────────────────────────

export default function VoiceInput({
	value = "",
	onChange,
	label,
	placeholder,
	rows = 3,
	required = false,
	helperText,
	error,
}) {
	const [listening, setListening] = useState(false);
	const [interim, setInterim] = useState("");
	const recognitionRef = useRef(null);

	const startListening = useCallback(() => {
		if (!SpeechRecognition) return;

		const recognition = new SpeechRecognition();
		recognition.lang = "it-IT";
		recognition.continuous = true;
		recognition.interimResults = true;

		recognition.onresult = (event) => {
			let finalTranscript = "";
			let interimTranscript = "";

			for (let i = event.resultIndex; i < event.results.length; i++) {
				const transcript = event.results[i][0].transcript;
				if (event.results[i].isFinal) {
					finalTranscript += transcript;
				} else {
					interimTranscript += transcript;
				}
			}

			if (finalTranscript) {
				const separator = value && !value.endsWith(" ") && !value.endsWith("\n") ? " " : "";
				onChange(value + separator + finalTranscript);
				setInterim("");
			} else {
				setInterim(interimTranscript);
			}
		};

		recognition.onerror = () => {
			setListening(false);
			setInterim("");
		};

		recognition.onend = () => {
			setListening(false);
			setInterim("");
		};

		recognitionRef.current = recognition;
		recognition.start();
		setListening(true);
	}, [value, onChange]);

	const stopListening = useCallback(() => {
		if (recognitionRef.current) {
			recognitionRef.current.stop();
			recognitionRef.current = null;
		}
		setListening(false);
		setInterim("");
	}, []);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			if (recognitionRef.current) {
				recognitionRef.current.stop();
			}
		};
	}, []);

	const supported = !!SpeechRecognition;

	return (
		<div className="vi">
			{label && (
				<div className="field-label-container">
					<div className="field-label">{label}</div>
					{required && <div className="field-required">*</div>}
				</div>
			)}

			<div className={`vi__wrap ${listening ? "vi__wrap--active" : ""} ${error ? "vi__wrap--error" : ""}`}>
				<textarea
					className="vi__textarea"
					value={value + (interim ? (value ? " " : "") + interim : "")}
					onChange={(e) => {
						if (!listening) onChange(e.target.value);
					}}
					placeholder={placeholder || label}
					rows={rows}
					readOnly={listening}
				/>

				{supported && (
					<button
						type="button"
						className={`vi__mic ${listening ? "vi__mic--active" : ""}`}
						onClick={listening ? stopListening : startListening}
						title={listening ? "Ferma registrazione" : "Registra voce"}
					>
						{listening ? (
							<>
								<MicOffIcon sx={{fontSize: 18}} />
								<span className="vi__mic-pulse" />
							</>
						) : (
							<MicIcon sx={{fontSize: 18}} />
						)}
					</button>
				)}
			</div>

			{listening && (
				<div className="vi__status">
					<span className="vi__status-dot" />
					Sto ascoltando... parla ora
				</div>
			)}

			{error && helperText && (
				<p className="MuiFormHelperText-root Mui-error" style={{color: "var(--color-critical)"}}>{helperText}</p>
			)}
		</div>
	);
}
